import React, {useState, useEffect, useCallback} from 'react';
import {
    Container,
    Paper,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import axiosInstance from "../AxiosInstance";
import Box from "@mui/material/Box";
import XeroIntegrationConnect from "./XeroIntegrationConnect";
import IntegrationProviderSelect from "./IntegrationProviderSelect";
import UnleashedIntegrationSettings from "./UnleashedIntegrationSettings";

const INTEGRATION_PROVIDER_UNLEASHED = 1;
const INTEGRATION_PROVIDER_XERO = 3;

function ThirdPartyIntegrationsPage() {
    const [organisationIntegrationId, setOrganisationIntegrationId] = useState(null); // Optional ID
    const [providerId, setProviderId] = useState(INTEGRATION_PROVIDER_UNLEASHED);
    const [providerOptions, setProviderOptions] = useState([
        { id: INTEGRATION_PROVIDER_UNLEASHED, name: 'Unleashed' },
        { id: INTEGRATION_PROVIDER_XERO, name: 'Xero' }
    ]);
    const [lockedProviderId, setLockedProviderId] = useState(null);
    const [apiId, setApiId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [integrationEnabled, setIntegrationEnabled] = useState(false); // State for the toggle switch
    const [isConnected, setIsConnected] = useState(false);
    const [connectionLabel, setConnectionLabel] = useState('');
    const [loading, setLoading] = useState(true); // Loading state
    const [savingSettings, setSavingSettings] = useState(false); // Loading state for settings save
    const [loadingProductGroups, setLoadingProductGroups] = useState(false); // Loading state for product groups
    const [error, setError] = useState(''); // Error state
    const [successMessage, setSuccessMessage] = useState(''); // Success notification state
    const [activeStep, setActiveStep] = useState(0); // Current step in the stepper
    const [productGroups, setProductGroups] = useState([]); // Available product groups from Unleashed
    const [selectedProductGroups, setSelectedProductGroups] = useState([]); // Selected product groups
    const [allProductsSelected, setAllProductsSelected] = useState(false); // Flag for "all products" option
    const [includeNonSellable, setIncludeNonSellable] = useState(false);
    const [autoDisabledMessage, setAutoDisabledMessage] = useState(null);

    const fetchIntegration = useCallback(async (selectedProviderId) => {
        const response = await axiosInstance.get('/settings', {
            params: { providerId: selectedProviderId }
        });
        const data = response.data;

        setOrganisationIntegrationId(data.organisationIntegrationId || null);
        setApiId(data.apiId || '');
        setApiKey(data.apiKey || '');
        setIntegrationEnabled(data.isEnabled || false);
        setIsConnected(data.isConnected || false);
        setConnectionLabel(data.connectionLabel || '');
        setSelectedProductGroups(data.productGroups || []);
        setAllProductsSelected(data.allProducts || false);
        setIncludeNonSellable(data.includeNonSellable || false);
        setAutoDisabledMessage(data.autoDisabledMessage || null);
    }, []);

    const initializePage = useCallback(async () => {
        setLoading(true);

        try {
            const params = new URLSearchParams(window.location.search);
            const providerParam = params.get('provider');
            const status = params.get('status');
            const message = params.get('message');

            const providersResponse = await axiosInstance.get('/settings/providers');
            const providers = providersResponse.data.providers || [];
            setProviderOptions(providers);
            setLockedProviderId(providersResponse.data.lockedProviderId || null);

            const requestedProviderId = providersResponse.data.lockedProviderId
                || (providerParam === 'xero'
                ? INTEGRATION_PROVIDER_XERO
                : providersResponse.data.selectedProviderId || INTEGRATION_PROVIDER_UNLEASHED);

            setProviderId(requestedProviderId);
            await fetchIntegration(requestedProviderId);

            if (status === 'connected')
                setSuccessMessage('Xero connected successfully.');
            else if (status === 'error')
                setError(message || 'Failed to connect to Xero.');

            if (status || providerParam || message) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch organisation integration. Please try again later.'
            );
        } finally {
            setLoading(false); // Stop loading spinner
        }
    }, [fetchIntegration]);

    useEffect(() => {
        initializePage();
    }, [initializePage]);

    const persistIntegration = async (overrides = {}) => {
        setSavingSettings(true);
        try {
            const payload = {
                organisationIntegrationId,
                integrationProviderId: providerId,
                apiId: providerId === INTEGRATION_PROVIDER_UNLEASHED ? (apiId || null) : null,
                apiKey: providerId === INTEGRATION_PROVIDER_UNLEASHED ? (apiKey || null) : null,
                isEnabled: integrationEnabled,
                productGroups: selectedProductGroups,
                allProductsSelected,
                includeNonSellable,
                ...overrides
            };

            await axiosInstance.put('/settings', payload);
            await fetchIntegration(providerId);
            setSuccessMessage('Changes saved successfully!');

            if ((overrides.isEnabled ?? integrationEnabled) === true) {
                setAutoDisabledMessage(null);
            }

            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to save organisation integration. Please try again later.'
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const handleToggle = async (event) => {
        const isEnabled = event.target.checked;
        setIntegrationEnabled(isEnabled);

        if (!isEnabled && activeStep !== 0)
            setActiveStep(0);

        await persistIntegration({ isEnabled });
    };

    const fetchProductGroups = async (id, key) => {
        setLoadingProductGroups(true);
        try {
            const response = await axiosInstance.post('/settings/unleashedproductgroups', {
                apiId: id || apiId,
                apiKey: key || apiKey
            });
            setProductGroups(response.data || []);
            return true;
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch product groups from Unleashed. Please check your API credentials.'
            );
            return false;
        } finally {
            setLoadingProductGroups(false);
        }
    };

    const handleNext = async () => {
        if (providerId !== INTEGRATION_PROVIDER_UNLEASHED)
            return;

        if (activeStep === 0) {
            // Validate required fields
            if (!apiId || !apiKey) {
                setError('Please enter both API ID and API Key.');
                return;
            }
            
            // Fetch product groups before proceeding to next step
            const success = await fetchProductGroups();
            if (success) {
                setActiveStep(1);
            }
        } else {
            await handleSave();
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleAllProductsChange = (event) => {
        const checked = event.target.checked;
        setAllProductsSelected(checked);
        if (checked) {
            // Clear selected product groups when "all products" is selected
            setSelectedProductGroups([]);
        }
    };

    const handleProductGroupsChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedProductGroups(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSave = async () => {
        await persistIntegration();
        setActiveStep(0);
    };

    const handleProviderChange = async (nextProviderId) => {
        setProviderId(nextProviderId);
        setActiveStep(0);
        setLoading(true);

        try {
            await fetchIntegration(nextProviderId);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to load organisation integration.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleXeroConnect = async () => {
        setSavingSettings(true);
        try {
            const response = await axiosInstance.post('/xero/connect', {
                frontendOrigin: window.location.origin
            });
            window.location.assign(response.data.authorizationUrl);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to start Xero connection. Please try again later.'
            );
            setSavingSettings(false);
        }
    };

    const steps = ['API Credentials', 'Product Configuration'];
    const canEnableUnleashed = apiId && apiKey && (allProductsSelected || selectedProductGroups.length > 0);

    return (
        <Box className="component-container">
            <Container component="main" maxWidth="sm">
                <Paper
                    elevation={3}
                    style={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography component="h1" variant="h5" gutterBottom>
                        Third Party Integration
                    </Typography>

                    {loading ? (
                        <CircularProgress style={{marginTop: '20px'}}/>
                    ) : (
                        <>
                            {!lockedProviderId && (
                                <IntegrationProviderSelect
                                    providerId={providerId}
                                    providerOptions={providerOptions}
                                    onChange={handleProviderChange}
                                />
                            )}

                            {providerId === INTEGRATION_PROVIDER_UNLEASHED ? (
                                <UnleashedIntegrationSettings
                                    activeStep={activeStep}
                                    steps={steps}
                                    apiId={apiId}
                                    apiKey={apiKey}
                                    integrationEnabled={integrationEnabled}
                                    canEnableIntegration={canEnableUnleashed}
                                    savingSettings={savingSettings}
                                    loadingProductGroups={loadingProductGroups}
                                    autoDisabledMessage={autoDisabledMessage}
                                    productGroups={productGroups}
                                    selectedProductGroups={selectedProductGroups}
                                    allProductsSelected={allProductsSelected}
                                    includeNonSellable={includeNonSellable}
                                    onApiIdChange={setApiId}
                                    onApiKeyChange={setApiKey}
                                    onToggleEnabled={(checked) => handleToggle({ target: { checked } })}
                                    onAllProductsChange={(checked) => handleAllProductsChange({ target: { checked } })}
                                    onProductGroupsChange={(value) => setSelectedProductGroups(value)}
                                    onIncludeNonSellableChange={setIncludeNonSellable}
                                    onBack={handleBack}
                                    onNext={handleNext}
                                />
                            ) : (
                                <XeroIntegrationConnect
                                    isConnected={isConnected}
                                    isEnabled={integrationEnabled}
                                    isSaving={savingSettings}
                                    connectionLabel={connectionLabel}
                                    autoDisabledMessage={autoDisabledMessage}
                                    onConnect={handleXeroConnect}
                                    onToggleEnabled={(checked) => handleToggle({ target: { checked } })}
                                />
                            )}
                        </>
                    )}
                </Paper>

                {/* Snackbar for Error Notification */}
                <Snackbar
                    open={!!error}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setError('')}
                >
                    <Alert onClose={() => setError('')} severity="error" sx={{width: '100%'}}>
                        {error}
                    </Alert>
                </Snackbar>

                {/* Snackbar for Success Notification */}
                <Snackbar
                    open={!!successMessage}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setSuccessMessage('')}
                >
                    <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{width: '100%'}}>
                        {successMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default ThirdPartyIntegrationsPage;
